import { create } from 'zustand';
import { DEFAULT_PERSISTED_QUOTE_EDITOR_STATE, normalizePersistedQuoteEditorState } from '@shared/quote-editor-state';
import type { PdfImportOfferStateDraft } from '@shared/pdf-import-contract';
import { applyQuoteNumbering } from '@shared/quote-numbering';
import { Position, PositionsAufmass, PositionsKalkulation, QuoteHeader, QuoteItem, ExcelRow } from '@shared/schema';
import {
  KALKULATION_FALLBACK_DEFAULTS,
  darfKalkulationPersistiertWerden,
  einheitspreisAusKalkulation,
  istKalkulationGueltigFuerEpAbleitung,
  type KalkulationsDefaults,
} from '@shared/kalkulation';
import { v4 as uuidv4 } from 'uuid';
import { databaseService } from './database-service';
import { requestNextQuoteNumber } from './quote-number';
import { composeGAEBDescription, normalizeGAEBLongText } from './gaeb-text-utils';
import {
  getLineNetAfterDiscount,
  normalizeVatRate,
  toSafeNumber,
  isExcludedFromMainSum,
  computeQuoteTotals,
} from './quote-calculations';

type PositionEditHistoryEntry = {
  positionId: number;
  previousPosition: Position;
  previousQuoteItem?: QuoteItem;
  previousOriginalPrice?: number;
};

export interface QuotePersistenceIdentity {
  slot: number;
  storageRevision: number;
}

export function normalizeLoadedQuoteStorageRevision(storageRevision: unknown): number {
  if (storageRevision === undefined) {
    return 0;
  }
  if (
    typeof storageRevision !== 'number'
    || !Number.isSafeInteger(storageRevision)
    || storageRevision < 1
  ) {
    throw new Error(`Invalid persisted storageRevision: ${String(storageRevision)}`);
  }
  return storageRevision;
}

interface OfferState {
  // Data from the Excel file
  positions: Position[];
  initialPositionsOrder: Position[]; // Store the original order for reset functionality
  
  // Search and filter state
  searchTerm: string;
  nameSearchTerm: string;
  currentPage: number;
  pageSize: number;
  sortField: keyof Position | null;
  sortDirection: 'asc' | 'desc';
  
  // Quote building state
  quoteItems: QuoteItem[];
  quoteHeader: QuoteHeader;
  
  // Gewerk organization
  gewerkOrder: string[]; // Array of Gewerk names in custom order
  
  // UI state
  isDataLoaded: boolean;
  leftPaneWidth: string;

  // Kalkulation (Tiefen-/Blitzkalkulation)
  kalkulationsDefaults: KalkulationsDefaults; // globaler Stundensatz + Material-Aufschlag%
  kalkulationsView: 'standard' | 'blitz'; // Maskenwechsel Angebotstabelle <-> Blitzkalkulation

  // Aufmaß (Mengenfortschreibung)
  aufmassView: boolean; // true = Aufmaß-Maske statt Angebotstabelle

  // GAEB state
  isGAEBDatabase: boolean;
  gaebMetadata: import('../../../shared/types').GAEBMetadata | null;
  currentDatabaseId: string | null; // Track current database ID for export
  
  // Quote tracking
  currentQuoteSlot: number | null; // Track which quote slot is loaded for voice iteration
  currentQuoteStorageRevision: number | null;
  offerDocumentEpoch: number;
  isVoiceQuote: boolean; // Track if current quote came from voice
  
  // Discount management
  discountAmount: number; // Discount value (percentage or absolute)
  discountType: 'percent' | 'absolute'; // Type of discount
  discountApplicationMode: 'total' | 'distributed'; // Apply to total or distributed to items
  originalPrices: Record<number, number>; // Store original prices before discount application (id -> price)
  isDiscountAppliedToPrices: boolean; // Track if discount has been applied to individual prices
  editHistory: PositionEditHistoryEntry[]; // Track inline edit history for undo operations

  // F2 Nummerierung: 'manual' (Default, Bestandsschutz — importierte OZ bleiben) oder
  // 'auto' (nach explizitem "Neu nummerieren"-Klick; Strukturänderungen nummerieren nach).
  numberingMode: 'manual' | 'auto';
  
  // Actions
  // options.preserveNumberingMode: nur für inkrementelles Anhängen im Editor (addNewRow);
  // Loader (neues Dokument) lassen den Default → numberingMode reset auf 'manual'.
  setPositions: (positions: Position[], options?: { preserveNumberingMode?: boolean }) => void;
  replaceWithPdfImport: (draft: PdfImportOfferStateDraft) => void;
  loadDatabaseData: (databaseId: string) => Promise<void>;
  loadQuoteData: (slot: number) => Promise<void>; // NEW: Load quote with quantities
  setSearchTerm: (term: string) => void;
  setNameSearchTerm: (term: string) => void;
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
  setSort: (field: keyof Position | null, direction: 'asc' | 'desc') => void;
  updateQuantity: (id: number, menge: number) => void;
  updatePosition: (id: number, updates: Partial<Position>) => void;
  // P1 (Fix, Codex-Fund): gibt ein Erfolgssignal zurück (false = abgelehnt, siehe
  // darfKalkulationPersistiertWerden weiter unten) — Aufrufer DÜRFEN false NICHT als
  // Erfolg behandeln (kein stiller Fehlpreis / keine falsche "übernommen"-Meldung).
  updatePositionKalkulation: (id: number, kalkulation: PositionsKalkulation | null) => boolean;
  setKalkulationsDefaults: (defaults: Partial<KalkulationsDefaults>) => void;
  setKalkulationsView: (view: 'standard' | 'blitz') => void;
  updatePositionAufmass: (id: number, aufmass: PositionsAufmass | null) => void;
  setAufmassView: (open: boolean) => void;
  updateQuoteHeader: (key: keyof QuoteHeader, value: string | number) => void;
  setQuoteHeader: (header: QuoteHeader) => void;
  setQuoteItems: (items: QuoteItem[]) => void;
  reorderPositions: (fromId: number, toId: number) => void;
  removePosition: (id: number) => void;
  // F2: GAEB-konforme OZ-Neuvergabe (1.2.1) aus shared/quote-numbering.ts — der EINZIGE
  // Weg, positionNumber neu zu vergeben. onlyIfAuto=true nummeriert nur nach, wenn der
  // Nutzer den Auto-Modus zuvor explizit aktiviert hat (Bestandsschutz für Import-OZ).
  renumberPositions: (options?: { onlyIfAuto?: boolean }) => void;
  updateItemDiscount: (id: number, rabattWert: number, rabattTyp: 'percent' | 'absolute') => void;
  clearItemDiscount: (id: number) => void;
  // Positionsart einer Zeile setzen (Normal/Bedarf/Alternativ, sich gegenseitig ausschließend).
  setItemPositionType: (id: number, type: 'normal' | 'bedarf' | 'alternativ') => void;
  // Inkrementeller Append im SELBEN Dokument (addNewRow) — im Gegensatz zum Loader-Pfad
  // setPositions bleiben gewerkOrder, editHistory, Suche und numberingMode erhalten.
  appendPosition: (position: Position, quoteItem: QuoteItem) => void;
  setGlobalVatRate: (rate: number) => void;
  setGewerkOrder: (order: string[]) => void;
  updateGewerkOrder: (newOrder: string[]) => void;
  reorderGewerk: (fromIndex: number, toIndex: number) => void;
  resetSearch: () => void;
  setLeftPaneWidth: (width: string) => void;
  setCurrentQuotePersistence: (identity: QuotePersistenceIdentity | null) => void;
  setIsVoiceQuote: (isVoice: boolean) => void; // NEW: Track voice origin
  undoLastEdit: () => void;

  // Discount actions
  setDiscount: (amount: number, type: 'percent' | 'absolute', applyToPrices?: boolean) => void;
  setDiscountApplicationMode: (mode: 'total' | 'distributed') => void;
  applyDistributedDiscount: () => void; // Apply discount to all Einzelpreise
  clearDiscount: () => void;

  // GAEB actions
  updateGAEBEntry: (id: number, updates: Partial<import('../../../shared/types').GAEBEntry>) => void;
  updateGAEBMetadata: (updates: Partial<import('../../../shared/types').GAEBMetadata>) => void;
  convertGAEBToOffer: () => void; // Convert GAEB database to regular offer

  clearAll: () => void;
  // Start a fresh empty quote (SimpleTable + autosave). allocateNumber=false
  // unterdrückt die Nummernkreis-Vergabe (Nachtrags-Brücke nummeriert selbst).
  initNewQuote: (options?: { allocateNumber?: boolean }) => void;
  // Auffangnetz für alle Editor-Pfade (z.B. Angebot aus Datenbank ohne
  // initNewQuote): vergibt eine Nummer, falls der Header noch keine hat.
  // Pro Dokument-Epoch höchstens einmal wirksam.
  ensureQuoteNumberAllocated: () => void;

  // Computed functions for sidebar
  getPositionsWithQuantities: () => QuoteItem[];
  getTotalsByGewerk: () => Array<{ gewerk: string; total: number; count: number }>;
  getGrandTotal: () => { net: number; tax: number; gross: number };
}

// angebot_nr startet LEER: die Nummer kommt fortlaufend aus dem serverseitigen
// Nummernkreis (scheduleQuoteNumberAllocation), nie mehr als Zufallszahl.
const DEFAULT_HEADER: QuoteHeader = {
  kunde: '',
  anschrift: '',
  angebot_nr: '',
  datum: new Date().toISOString().split('T')[0],
  mwstSatz: 19,
};

/**
 * Holt asynchron die nächste Nummer aus dem Nummernkreis und trägt sie in den
 * Header ein — nur solange (a) noch dasselbe Dokument offen ist (Epoch-Guard)
 * und (b) niemand inzwischen von Hand eine Nummer eingetragen hat.
 * Pro Dokument-Epoch wird höchstens EINE Nummer angefordert (Dedup), auch wenn
 * mehrere Stellen (initNewQuote + Sidebar-Auffangnetz) die Vergabe anstoßen.
 * Schlägt der Server fehl, bleibt das Feld leer (kein Zufalls-Fallback).
 */
let lastQuoteNumberAllocationEpoch: number | null = null;

function scheduleQuoteNumberAllocation(
  get: () => Pick<OfferState, 'offerDocumentEpoch' | 'quoteHeader'>,
  set: (partial: Partial<Pick<OfferState, 'quoteHeader'>>) => void,
): void {
  const epoch = get().offerDocumentEpoch;
  if (lastQuoteNumberAllocationEpoch === epoch) return;
  lastQuoteNumberAllocationEpoch = epoch;
  void requestNextQuoteNumber()
    .then((number) => {
      if (!number) return;
      const state = get();
      if (state.offerDocumentEpoch !== epoch) return;
      if ((state.quoteHeader.angebot_nr || '').trim()) return;
      set({ quoteHeader: { ...state.quoteHeader, angebot_nr: number } });
    })
    .catch(() => {});
}

function sortQuoteItemsByPositionsOrder(quoteItems: QuoteItem[], positions: Position[]): QuoteItem[] {
  const positionOrder = new Map<number, number>(
    positions.map((position, index) => [position.id, index]),
  );

  return [...quoteItems].sort((a, b) => {
    const aOrder = positionOrder.get(a.id) ?? Number.MAX_SAFE_INTEGER;
    const bOrder = positionOrder.get(b.id) ?? Number.MAX_SAFE_INTEGER;
    if (aOrder !== bOrder) {
      return aOrder - bOrder;
    }
    return a.id - b.id;
  });
}

function deriveVatRateFromItems(items: QuoteItem[]): 0 | 7 | 19 {
  if (!Array.isArray(items) || items.length === 0) {
    return 19;
  }

  const normalizedRates = new Set(items.map((item) => normalizeVatRate((item as any).umsatzsteuer)));
  if (normalizedRates.size === 1) {
    return normalizedRates.values().next().value ?? 19;
  }
  return 19;
}

export const useOfferStore = create<OfferState>()(
    (set, get) => ({
      positions: [],
      initialPositionsOrder: [], // Store the original order
      searchTerm: '',
      nameSearchTerm: '',
      currentPage: 1,
      pageSize: 25,
      sortField: null,
      sortDirection: 'asc',
      quoteItems: [],
      quoteHeader: { ...DEFAULT_HEADER },
      gewerkOrder: [], // Array of Gewerk names in custom order
      isDataLoaded: false,
      leftPaneWidth: (typeof window !== 'undefined' && localStorage.getItem('offer-leftPaneWidth')) || '50%',
      kalkulationsDefaults: { ...KALKULATION_FALLBACK_DEFAULTS },
      kalkulationsView: 'standard',
      aufmassView: false,
      currentQuoteSlot: null, // Track current quote slot
      currentQuoteStorageRevision: null,
      offerDocumentEpoch: 0,
      isVoiceQuote: false, // Track if from voice
      isGAEBDatabase: false, // Track if current database is GAEB
      gaebMetadata: null, // GAEB metadata when applicable
      currentDatabaseId: null, // Track current database ID for export
      ...DEFAULT_PERSISTED_QUOTE_EDITOR_STATE,
      editHistory: [],
      setPositions: (positions, options) => {
        // Clear search cache when setting new positions
        searchCache.clearCache();

        // Extract unique Gewerk values for initial order
        // Get all non-empty Gewerk values
        const gewerkeList = positions
          .map(pos => pos.gewerk || '')
          .filter(g => g !== '');

        // Use array reduce to filter unique values
        const uniqueGewerke = gewerkeList.reduce((unique: string[], gewerk) => {
          if (!unique.includes(gewerk)) {
            unique.push(gewerk);
          }
          return unique;
        }, []);

        // Reset all quantity values and quote items when loading new positions
        set((state) => ({
          positions,
          initialPositionsOrder: [...positions], // Store a copy of the original order
          isDataLoaded: true,
          quoteItems: [], // Reset quote items when loading new Excel
          editHistory: [],
          searchTerm: '',
          nameSearchTerm: '', // Reset name search too
          currentPage: 1,
          sortField: null, // Reset sorting to initial state
          sortDirection: 'asc',
          gewerkOrder: uniqueGewerke, // Initialize Gewerk order with default alphabetical order
          // F2/Bestandsschutz (Codex-P1): setPositions ist der Loader-Eintritt für ein
          // NEUES Dokument (Excel/PDF/DB-Import) — der Auto-Nummerierungs-Modus des
          // vorherigen Angebots darf NICHT kleben, sonst renumbert der erste Struktur-
          // Edit die importierten OZ still. Nur der inkrementelle Append im Editor
          // (addNewRow) behält den Modus explizit bei.
          ...(options?.preserveNumberingMode
            ? {}
            : {
                numberingMode: 'manual' as const,
                currentQuoteSlot: null,
                currentQuoteStorageRevision: null,
                offerDocumentEpoch: state.offerDocumentEpoch + 1,
              }),
        }));
      },

      replaceWithPdfImport: (draft) => {
        searchCache.clearCache();
        const vatRate = normalizeVatRate(draft.quoteHeader.mwstSatz);
        const quoteItems = draft.quoteItems.map((item) => ({
          ...item,
          umsatzsteuer: vatRate,
        }));
        const positions = draft.positions.map((position) => ({
          ...position,
          umsatzsteuer: vatRate,
        }));
        const gewerkOrder = Array.from(new Set(
          quoteItems
            .map((item) => item.gewerk?.trim() || '')
            .filter(Boolean),
        ));

        // A PDF import is a new document boundary. Apply every dependent field in
        // one Zustand transaction so autosave can never observe a mixed old/new
        // state or overwrite the previously loaded quote slot.
        set((state) => ({
          quoteHeader: { ...draft.quoteHeader, mwstSatz: vatRate },
          quoteItems,
          positions,
          initialPositionsOrder: [...positions],
          gewerkOrder,
          isDataLoaded: true,
          currentQuoteSlot: null,
          currentQuoteStorageRevision: null,
          isVoiceQuote: false,
          isGAEBDatabase: false,
          gaebMetadata: null,
          currentDatabaseId: null,
          searchTerm: '',
          nameSearchTerm: '',
          currentPage: 1,
          sortField: null,
          sortDirection: 'asc',
          kalkulationsView: 'standard',
          aufmassView: false,
          ...DEFAULT_PERSISTED_QUOTE_EDITOR_STATE,
          editHistory: [],
          offerDocumentEpoch: state.offerDocumentEpoch + 1,
        }));
      },

      loadDatabaseData: async (databaseId) => {
        try {
          // Load database from service
          const database = await databaseService.loadDatabase(databaseId);

          // Check if this is a GAEB database
          const isGAEB = database.type === 'gaeb-import';
          const gaebMetadata = database.gaebMetadata || null;

          // Convert database entries to positions format
          const positions: Position[] = database.entries.map((entry: any, index) => {
            const normalizedBasePrice = (() => {
              const candidate = entry.einzelpreis ?? entry.preis;
              if (typeof candidate === 'number') {
                return candidate;
              }
              if (typeof candidate === 'string') {
                const parsed = parseFloat(candidate.replace(',', '.'));
                return Number.isFinite(parsed) ? parsed : 0;
              }
              return 0;
            })();

            const gaebEffectivePrice = isGAEB
              ? (typeof entry.ourPrice === 'number'
                  ? entry.ourPrice
                  : typeof entry.originalPrice === 'number'
                    ? entry.originalPrice
                    : normalizedBasePrice)
              : normalizedBasePrice;

            const basePosition: Position = {
              id: index + 1,
              originalId: entry.id || entry.artikelId || String(index + 1),
              name: entry.name || '',
              beschreibung: entry.beschreibung || '',
              gewerk: entry.gewerk || '',
              // F2: OZ + Untertitel aus der gespeicherten DB übernehmen — sonst verliert
              // die Struktur (Titel/Untertitel/1.2.1) den Roundtrip über die Angebots-DB.
              positionNumber: entry.positionNumber || undefined,
              untergewerkName: entry.untergewerkName || undefined,
              einheit: entry.einheit || 'Stk',
              preis: isGAEB
                ? (Number.isFinite(gaebEffectivePrice) ? gaebEffectivePrice.toFixed(2) : '0.00')
                : (entry.einzelpreis?.toString() || entry.preis?.toString() || '0'),
              umsatzsteuer: parseFloat(entry.umsatzsteuer?.toString() || entry.mwst?.toString() || '19'),
              // Positionsart aus der gespeicherten DB übernehmen — sonst wird eine
              // Bedarfs-/Alternativposition beim Wiederladen still zur Normalposition.
              isBedarfsposition: Boolean(entry.isBedarfsposition) || undefined,
              isAlternativposition: Boolean(entry.isAlternativposition) || undefined,
            };

            // Add GAEB-specific fields if this is a GAEB database
            if (isGAEB) {
              const normalizedLongText = normalizeGAEBLongText(entry.longText);
              const shortText = entry.shortText || entry.name || basePosition.name || basePosition.beschreibung;
              const beschreibung = composeGAEBDescription({
                shortText,
                longText: normalizedLongText,
                ourComment: entry.ourComment,
              }) || basePosition.beschreibung;

              return {
                ...basePosition,
                // CRITICAL: Preserve artikelId for save/export matching
                // This must match the artikelId in the database JSON file
                artikelId: entry.artikelId,
                name: shortText || basePosition.name,
                beschreibung,
                positionNumber: entry.positionNumber || String(index + 1),
                type: entry.type || 'item',
                shortText: shortText || entry.beschreibung || '',
                longText: normalizedLongText,
                hierarchyLevel: entry.hierarchyLevel || 0,
                parentId: entry.parentId,
                ourPrice: entry.ourPrice,
                ourComment: entry.ourComment,
                originalPrice: entry.originalPrice,
                originalQuantity: entry.originalQuantity,
                adjustedQuantity: entry.adjustedQuantity,
                // CRITICAL: Copy menge for GAEB→Offer conversion
                menge: entry.menge,
                // CRITICAL: Copy Bedarfsposition flag for proper display (B badge, amber styling)
                isBedarfsposition: entry.isBedarfsposition || false,
                // Alternativposition ebenfalls erhalten (im Editor gesetzt + gespeichert)
                isAlternativposition: entry.isAlternativposition || false,
                // Copy TextComplement structures for T badge
                gaebTextComplementMarkers: entry.gaebTextComplementMarkers,
                gaebTextComplementStructures: entry.gaebTextComplementStructures,
              } as Position & import('../../../shared/types').GAEBEntry;
            }

            return basePosition;
          });
          
          // Update quote header with project info if available
          if (database.projectInfo) {
            set({ 
              quoteHeader: {
                ...get().quoteHeader,
                angebot_nr: database.projectInfo.projectNumber || get().quoteHeader.angebot_nr,
                kunde: database.projectInfo.customerName || get().quoteHeader.kunde,
                datum: database.projectInfo.date || get().quoteHeader.datum,
                bauvorhaben: database.projectInfo.description || get().quoteHeader.bauvorhaben,
              }
            });
          }
          
          // Use setPositions to properly initialize the data
          get().setPositions(positions);

          // Set GAEB flags and metadata
          set({
            isGAEBDatabase: isGAEB,
            gaebMetadata: gaebMetadata,
            currentQuoteSlot: null, // Reset quote tracking (loading database, not quote)
            currentQuoteStorageRevision: null,
            isVoiceQuote: false,
            currentDatabaseId: databaseId // Update current database ID
          });
          
          // Check if database entries have quantities and create quote items
          // For GAEB databases, also check adjustedQuantity and originalQuantity
          const entriesWithQuantities = database.entries.filter((entry: any) => {
            // Skip titles - only include items
            if (entry.type === 'title') {
              return false;
            }
            // For GAEB: use adjustedQuantity > originalQuantity > menge
            const quantity = entry.adjustedQuantity ?? entry.originalQuantity ?? parseFloat(entry.menge?.toString() || '0');
            return quantity > 0;
          });

          if (entriesWithQuantities.length > 0) {
            console.log(`Found ${entriesWithQuantities.length} entries with quantities in database`);

            // Create quote items from entries that have quantities
            const quoteItems: QuoteItem[] = entriesWithQuantities.map((entry: any, index) => {
              const positionIndex = database.entries.findIndex((e: any) => e === entry);
              const position = positions[positionIndex];

              // For GAEB: use effective quantity and price
              const effectiveQuantity = entry.adjustedQuantity ?? entry.originalQuantity ?? parseFloat(entry.menge?.toString() || '0');

              // For GAEB: ourPrice takes precedence, even if 0!
              // 0 is a valid price (included service), only skip if undefined
              const hasOurPrice = typeof entry.ourPrice === 'number';
              const hasOriginalPrice = typeof entry.originalPrice === 'number';
              const effectivePrice = hasOurPrice
                ? entry.ourPrice
                : (hasOriginalPrice ? entry.originalPrice : Number(position.preis));

              return {
                id: position.id,
                originalId: position.originalId || entry.artikelId || String(position.id),
                positionNumber: entry.positionNumber || undefined,
                name: position.name || '',
                beschreibung: position.beschreibung,
                gewerk: position.gewerk || undefined,
                // F2: Untertitel mitnehmen (gleiche Builder-Falle wie Positionsart).
                untergewerkName: (entry as any).untergewerkName || (position as any).untergewerkName || undefined,
                einheit: position.einheit,
                preis: effectivePrice,
                umsatzsteuer: Number(position.umsatzsteuer),
                menge: effectiveQuantity,
                budgetTaskId: (entry as any).budgetTaskId || (entry as any).taskId || (position as any).budgetTaskId || undefined,
                kalkulation: position.kalkulation || undefined,
                aufmass: position.aufmass || undefined,
                // Positionsart aus dem GAEB-Eintrag mitnehmen — sonst fällt eine
                // Bedarfsposition beim Übergang GAEB→Angebot still in die Hauptsumme.
                isBedarfsposition: Boolean((entry as any).isBedarfsposition) || undefined,
                isAlternativposition: Boolean((entry as any).isAlternativposition) || undefined,
              };
            });

            // Set the quote items to populate the Menge column
            set({ quoteItems });
            console.log(`Loaded ${quoteItems.length} quote items with quantities (including price=0 items)`);
          }

          const derivedRates = Array.from(new Set(positions.map((position) => normalizeVatRate(position.umsatzsteuer))));
          const derivedVatRate = derivedRates.length === 1 ? derivedRates[0] : 19;
          set({
            quoteHeader: {
              ...get().quoteHeader,
              mwstSatz: normalizeVatRate(get().quoteHeader.mwstSatz ?? derivedVatRate),
            },
          });
          
        } catch (error) {
          console.error('Failed to load database:', error);
          throw error;
        }
      },

      loadQuoteData: async (slot: number) => {
        try {
          console.log(`[Store] Loading quote from slot ${slot}...`);
          
          // Import quote service
          const { getQuoteDataBySlot } = await import('./quote-data-service');
          
          // Load quote from specific slot
          const quoteData = await getQuoteDataBySlot(slot);
          
          console.log(`[Store] Quote data loaded:`, {
            positions: quoteData.positions?.length || 0,
            items: quoteData.items?.length || 0,
            header: quoteData.header
          });

          const loadedItems = Array.isArray(quoteData.items)
            ? quoteData.items.map((item) => ({ ...item }))
            : [];

          const headerVatRateRaw = (quoteData.header as any)?.mwstSatz;
          const hasHeaderVatRate = headerVatRateRaw !== undefined && headerVatRateRaw !== null && headerVatRateRaw !== '';
          const headerVatRate = hasHeaderVatRate ? normalizeVatRate(headerVatRateRaw) : null;
          const fallbackVatRate = deriveVatRateFromItems(loadedItems as QuoteItem[]);
          const effectiveVatRate = headerVatRate ?? fallbackVatRate;
          
          // DEBUG STEP 2: add quoteHeader
          searchCache.clearCache();
          const normalizedHeader: QuoteHeader = {
            ...DEFAULT_HEADER,
            ...(quoteData.header || {}),
            mwstSatz: effectiveVatRate,
          };
          // DEBUG STEP 3: add quoteItems
          const normalizedItems = loadedItems.length > 0
            ? loadedItems.map((item) => ({ ...item, umsatzsteuer: effectiveVatRate }))
            : [];
          const persistedEditorState = normalizePersistedQuoteEditorState(quoteData.editorState);
          // DEBUG STEP 4: add positions + rest
          let normalizedPositions: Position[] = [];
          let uniqueGewerke: string[] = [];
          if (quoteData.positions && quoteData.positions.length > 0) {
            normalizedPositions = quoteData.positions.map((position) => ({
              ...position,
              umsatzsteuer: effectiveVatRate,
            }));
            uniqueGewerke = Array.from(
              new Set(normalizedPositions.map((p) => p.gewerk || '').filter((g) => g !== ''))
            );
          }
          set((state) => ({
            isDataLoaded: true,
            currentQuoteSlot: slot,
            currentQuoteStorageRevision: normalizeLoadedQuoteStorageRevision(
              quoteData.metadata.storageRevision,
            ),
            isVoiceQuote: false,
            quoteHeader: normalizedHeader,
            quoteItems: normalizedItems,
            positions: normalizedPositions,
            initialPositionsOrder: [...normalizedPositions],
            gewerkOrder: quoteData.gewerkOrder || uniqueGewerke,
            searchTerm: '',
            nameSearchTerm: '',
            currentPage: 1,
            sortField: null,
            sortDirection: 'asc',
            ...persistedEditorState,
            editHistory: [],
            offerDocumentEpoch: state.offerDocumentEpoch + 1,
          }));
          
        } catch (error) {
          console.error(`[Store] Failed to load quote from slot ${slot}:`, error);
          throw error;
        }
      },
      
      setSearchTerm: (term) => 
        set({ searchTerm: term, currentPage: 1 }),
      
      setNameSearchTerm: (term) => 
        set({ nameSearchTerm: term }),
      
      setPage: (page) => 
        set({ currentPage: page }),
      
      setPageSize: (size) => 
        set({ pageSize: size, currentPage: 1 }),
      
      setSort: (field, direction) => 
        set({ sortField: field, sortDirection: direction }),
      
      updateQuantity: (id, menge) => {
        try {
          // Allow larger quantities without an arbitrary limit
          const safeQuantity = Math.max(0, menge); // Just ensure it's not negative
          
          const { positions, quoteItems, originalPrices, isDiscountAppliedToPrices, discountAmount, discountType, discountApplicationMode, quoteHeader } = get();
          const position = positions.find(p => p.id === id);
          
          if (!position) return;
          
          const existingItemIndex = quoteItems.findIndex(item => item.id === id);
          
          if (safeQuantity <= 0 && existingItemIndex !== -1) {
            // Remove item if quantity is zero or negative
            const newOriginalPrices = { ...originalPrices };
            delete newOriginalPrices[id];
            
            set({
              quoteItems: quoteItems.filter(item => item.id !== id),
              originalPrices: newOriginalPrices
            });
          } else if (safeQuantity > 0) {
            // Ensure price is a valid number
            const price = typeof position.preis === 'string' 
              ? parseFloat(position.preis) 
              : position.preis;
            
            if (existingItemIndex !== -1) {
              // Update existing item - create new array to avoid mutations
              const bisher = quoteItems[existingItemIndex];
              const updatedItems = [...quoteItems];
              updatedItems[existingItemIndex] = {
                ...bisher,
                menge: safeQuantity,
                // Bauteilbezug (IFC-Herkunft) gilt nur für die übernommene Menge:
                // eine Handänderung löscht den Vermerk — er würde sonst lügen.
                // Unveränderte Menge (No-op) behält ihn.
                mengeHerkunft: bisher.menge === safeQuantity ? bisher.mengeHerkunft : undefined,
              };
              set({ quoteItems: updatedItems });
            } else {
              // Add new item
              const newOriginalPrices = { ...originalPrices };
              const originalPrice = isNaN(price) ? 0 : price;
              
              // Store original price for new item
              newOriginalPrices[position.id] = originalPrice;
              
              // Apply discount if currently in distributed mode (percentage only)
              let itemPrice = originalPrice;
              if (discountApplicationMode === 'distributed' && discountAmount > 0 && discountType === 'percent') {
                itemPrice = parseFloat((originalPrice * (1 - discountAmount / 100)).toFixed(2));
              }
              
              set({
                quoteItems: [
                  ...quoteItems, 
                  {
                    id: position.id,
                    originalId: position.originalId || String(position.id),
                    name: position.name || '',
                    beschreibung: position.beschreibung,
                    gewerk: position.gewerk || undefined,
                    einheit: position.einheit,
                    preis: itemPrice,
                    umsatzsteuer: normalizeVatRate(quoteHeader.mwstSatz ?? position.umsatzsteuer),
                    menge: safeQuantity,
                    kalkulation: position.kalkulation || undefined,
                    aufmass: position.aufmass || undefined,
                    // Positionsart von der Position übernehmen — sonst verliert eine
                    // menge=0→>0 wieder aktivierte Bedarfs-/Alternativposition ihr Flag.
                    isBedarfsposition: (position as any).isBedarfsposition || undefined,
                    isAlternativposition: (position as any).isAlternativposition || undefined,
                    // F2: OZ + Untertitel von der Position übernehmen (gleiche Falle) —
                    // sonst druckt eine re-aktivierte Zeile ohne OZ/Untertitel.
                    positionNumber: position.positionNumber || undefined,
                    untergewerkName: (position as any).untergewerkName || undefined,
                  }
                ],
                originalPrices: newOriginalPrices
              });
              // Re-Add hängt hinten an — im Auto-Modus die quoteItems zurück in die
              // positions-Reihenfolge bringen (renumber sortiert mit), sonst druckt
              // das Dokument [1.1, 1.3, 1.2].
              get().renumberPositions({ onlyIfAuto: true });
            }
          }
        } catch (error) {
          console.error('Error in updateQuantity:', error);
        }
      },
      
      updatePosition: (id, updates) => {
        const {
          positions,
          quoteItems,
          originalPrices,
          discountApplicationMode,
          discountAmount,
          discountType,
          editHistory
        } = get();

        const positionIndex = positions.findIndex((p) => p.id === id);
        if (positionIndex === -1) {
          return;
        }

        const currentPosition = positions[positionIndex];
        const previousPosition: Position = { ...currentPosition };

        const quoteItemIndex = quoteItems.findIndex((item) => item.id === id);
        const previousQuoteItem = quoteItemIndex !== -1 ? { ...quoteItems[quoteItemIndex] } : undefined;
        const previousOriginalPrice = originalPrices[id];

        const sanitizedUpdates: Partial<Position> = {};
        let priceNumericValue: number | undefined;
        let shouldUpdate = false;

        if (updates.name !== undefined) {
          const newValue = (updates.name ?? '').toString();
          if (newValue !== (currentPosition.name ?? '')) {
            sanitizedUpdates.name = newValue;
            shouldUpdate = true;
          }
        }

        if (updates.beschreibung !== undefined) {
          const newValue = (updates.beschreibung ?? '').toString();
          if (newValue !== (currentPosition.beschreibung ?? '')) {
            sanitizedUpdates.beschreibung = newValue;
            shouldUpdate = true;
          }
        }

        // Langtext (Kurz-/Langtext-Umschalter) — leerer String löscht den Langtext.
        if (updates.longText !== undefined) {
          const newValue = (updates.longText ?? '').toString();
          if (newValue !== (currentPosition.longText ?? '')) {
            sanitizedUpdates.longText = newValue;
            shouldUpdate = true;
          }
        }

        if (updates.gewerk !== undefined) {
          const newValue = (updates.gewerk ?? '').toString();
          if (newValue !== (currentPosition.gewerk ?? '')) {
            sanitizedUpdates.gewerk = newValue;
            shouldUpdate = true;
            // Metadatum wandert mit: untergewerkName === gewerk ist Mapping-Metadatum
            // (PDF-LV/Softec), KEIN echter Untertitel. Beim Gewerk-Umbenennen muss es
            // dem neuen Namen folgen, sonst würde der ALTE Gewerk-Name plötzlich als
            // echter Untertitel klassifiziert ("Elektro"-Kopf unter "Starkstrom").
            const currentSubtitle = ((currentPosition as any).untergewerkName ?? '').toString().trim();
            const currentGewerk = (currentPosition.gewerk ?? '').toString().trim();
            if (
              updates.untergewerkName === undefined &&
              currentSubtitle !== '' &&
              currentSubtitle === currentGewerk
            ) {
              sanitizedUpdates.untergewerkName = newValue;
            }
          }
        }

        // F2: Untertitel (Ebene 2) — leerer String löscht den Untertitel.
        if (updates.untergewerkName !== undefined) {
          const newValue = (updates.untergewerkName ?? '').toString();
          if (newValue !== (currentPosition.untergewerkName ?? '')) {
            sanitizedUpdates.untergewerkName = newValue;
            shouldUpdate = true;
          }
        }

        if (updates.einheit !== undefined) {
          const newValue = (updates.einheit ?? '').toString();
          if (newValue !== (currentPosition.einheit ?? '')) {
            sanitizedUpdates.einheit = newValue;
            shouldUpdate = true;
          }
        }

        if (updates.preis !== undefined) {
          const raw = typeof updates.preis === 'number'
            ? updates.preis
            : parseFloat(String(updates.preis).replace(',', '.'));

          if (!Number.isNaN(raw)) {
            const normalized = Math.max(0, Math.round(raw * 100) / 100);
            const normalizedString = normalized.toFixed(2);

            if (normalizedString !== String(currentPosition.preis ?? '')) {
              sanitizedUpdates.preis = normalizedString as unknown as Position['preis'];
              priceNumericValue = normalized;
              shouldUpdate = true;
              // Manuell erfasster Preis hebt "kein Preis erfasst" (preisFehlt) auf.
              if (currentPosition.preisFehlt) {
                sanitizedUpdates.preisFehlt = undefined;
              }
              // "aus Angebot X übernommen" dokumentiert den ALTEN Preisstand —
              // nach manueller Preisänderung würde der Vermerk lügen
              // (Muster mengeHerkunft in updateQuantity).
              if (currentPosition.positionsHerkunft) {
                sanitizedUpdates.positionsHerkunft = undefined;
              }
            }
          }
        }

        if (updates.umsatzsteuer !== undefined) {
          const raw = typeof updates.umsatzsteuer === 'number'
            ? updates.umsatzsteuer
            : parseInt(String(updates.umsatzsteuer), 10);

          if (!Number.isNaN(raw)) {
            const normalized = Math.max(0, Math.round(raw));
            if (normalized !== Number(currentPosition.umsatzsteuer ?? 0)) {
              sanitizedUpdates.umsatzsteuer = normalized as unknown as Position['umsatzsteuer'];
              shouldUpdate = true;
            }
          }
        }

        if (!shouldUpdate) {
          return;
        }

        const updatedPosition: Position = {
          ...currentPosition,
          ...sanitizedUpdates,
        };

        const newPositions = [...positions];
        newPositions[positionIndex] = updatedPosition;

        let newQuoteItems = quoteItems;
        let quoteItemsChanged = false;

        if (quoteItemIndex !== -1) {
          const updatedQuoteItem = { ...quoteItems[quoteItemIndex] };

          if (sanitizedUpdates.name !== undefined) {
            (updatedQuoteItem as any).name = sanitizedUpdates.name || '';
            quoteItemsChanged = true;
          }

          if (sanitizedUpdates.beschreibung !== undefined) {
            updatedQuoteItem.beschreibung = sanitizedUpdates.beschreibung || '';
            quoteItemsChanged = true;
          }

          if (sanitizedUpdates.longText !== undefined) {
            const trimmedLong = (sanitizedUpdates.longText || '').toString();
            updatedQuoteItem.longText = trimmedLong ? trimmedLong : undefined;
            quoteItemsChanged = true;
          }

          if (sanitizedUpdates.gewerk !== undefined) {
            const trimmed = sanitizedUpdates.gewerk?.toString().trim();
            updatedQuoteItem.gewerk = trimmed ? trimmed : undefined;
            quoteItemsChanged = true;
          }

          if (sanitizedUpdates.untergewerkName !== undefined) {
            const trimmed = sanitizedUpdates.untergewerkName?.toString().trim();
            (updatedQuoteItem as any).untergewerkName = trimmed ? trimmed : undefined;
            quoteItemsChanged = true;
          }

          if (sanitizedUpdates.einheit !== undefined) {
            updatedQuoteItem.einheit = sanitizedUpdates.einheit || '';
            quoteItemsChanged = true;
          }

          if (sanitizedUpdates.umsatzsteuer !== undefined) {
            updatedQuoteItem.umsatzsteuer = Number(sanitizedUpdates.umsatzsteuer);
            quoteItemsChanged = true;
          }

          if (priceNumericValue !== undefined) {
            let newItemPrice = priceNumericValue;
            if (discountApplicationMode === 'distributed' && discountAmount > 0 && discountType === 'percent') {
              newItemPrice = parseFloat((priceNumericValue * (1 - discountAmount / 100)).toFixed(2));
            }
            updatedQuoteItem.preis = newItemPrice;
            // Manuell erfasster Preis hebt "kein Preis erfasst" auf (siehe oben).
            if (updatedQuoteItem.preisFehlt) {
              updatedQuoteItem.preisFehlt = undefined;
            }
            // Preisstand-Vermerk "aus Angebot X" auch am quoteItem löschen (siehe oben).
            if (updatedQuoteItem.positionsHerkunft) {
              updatedQuoteItem.positionsHerkunft = undefined;
            }
            quoteItemsChanged = true;
          }

          if (quoteItemsChanged) {
            newQuoteItems = [...quoteItems];
            newQuoteItems[quoteItemIndex] = updatedQuoteItem;
          }
        }

        let newOriginalPrices = originalPrices;
        if (priceNumericValue !== undefined) {
          newOriginalPrices = { ...originalPrices, [id]: priceNumericValue };
        }

        const historyEntry: PositionEditHistoryEntry = {
          positionId: id,
          previousPosition,
          previousQuoteItem,
          previousOriginalPrice,
        };

        const historyCapacity = 20;
        const newHistory = [...editHistory, historyEntry].slice(-historyCapacity);

        // initialPositionsOrder speichert nur die REIHENFOLGE für resetSearch — Feld-Edits
        // müssen mitwandern, sonst verwirft "Suche zurücksetzen" z.B. einen frisch
        // gesetzten Untertitel/Gewerk/Preis (gleiche Falle wie F1-Positionsart-Flags).
        const newInitialOrder = (get().initialPositionsOrder || []).map((position) =>
          position.id === id ? { ...position, ...sanitizedUpdates } : position,
        );

        // F2: neu getipptes Gewerk (= neuer Titel) MUSS in gewerkOrder aufgenommen
        // werden — pdfkit/docx-template rendern nur Gewerke, die in gewerkOrder
        // stehen; sonst verschwindet die Position still aus dem PDF (Datenverlust).
        let newGewerkOrder = get().gewerkOrder;
        if (sanitizedUpdates.gewerk !== undefined && sanitizedUpdates.gewerk !== null) {
          const trimmedGewerk = sanitizedUpdates.gewerk.toString().trim();
          if (trimmedGewerk && !newGewerkOrder.includes(trimmedGewerk)) {
            if (newGewerkOrder.length === 0) {
              // Leere Order (= keine Nutzer-Sortierung): mit der Erst-Auftritts-
              // Reihenfolge ALLER Gewerke seeden — nur das neue aufzunehmen ließe
              // pdfkit/docx alle anderen verwerfen; gar nichts aufzunehmen ließe
              // OZ-Reihenfolge (Erst-Auftritt) und PDF-Reihenfolge (alphabetisch)
              // auseinanderlaufen ("2. Alpha" vor "1. Zulu").
              const seen: string[] = [];
              for (const pos of newPositions) {
                const g = (pos.gewerk || '').toString().trim();
                if (g && !seen.includes(g)) seen.push(g);
              }
              newGewerkOrder = seen;
            } else {
              newGewerkOrder = [...newGewerkOrder, trimmedGewerk];
            }
          }
        }

        set({
          positions: newPositions,
          initialPositionsOrder: newInitialOrder,
          quoteItems: newQuoteItems,
          originalPrices: newOriginalPrices,
          editHistory: newHistory,
          gewerkOrder: newGewerkOrder,
        });

        // Titel-/Untertitel-Wechsel ist eine Strukturänderung: im Auto-Modus OZ nachziehen.
        if (sanitizedUpdates.gewerk !== undefined || sanitizedUpdates.untergewerkName !== undefined) {
          get().renumberPositions({ onlyIfAuto: true });
        }
      },

      updatePositionKalkulation: (id, kalkulation) => {
        const {
          positions,
          quoteItems,
          originalPrices,
          discountApplicationMode,
          discountAmount,
          discountType,
          editHistory,
          kalkulationsDefaults,
        } = get();

        const positionIndex = positions.findIndex((p) => p.id === id);
        if (positionIndex === -1) {
          // P1 (Fix, Codex-Fund): Position existiert nicht (mehr) — nichts geschrieben,
          // Aufrufer MUSS das als Fehlschlag werten (kein stiller Fehlpreis).
          return false;
        }

        const currentPosition = positions[positionIndex];
        const previousPosition: Position = { ...currentPosition };
        const quoteItemIndex = quoteItems.findIndex((item) => item.id === id);
        const previousQuoteItem = quoteItemIndex !== -1 ? { ...quoteItems[quoteItemIndex] } : undefined;
        const previousOriginalPrice = originalPrices[id];

        const normalizedKalkulation = kalkulation && kalkulation.zeilen.length > 0 ? kalkulation : null;

        // P2-5 (Fix, Codex-Fund): eine BEFÜLLTE, aber für die EP-Ableitung UNGÜLTIGE
        // Kalkulation (Netto ≤ 0, z.B. ein globaler −100%-Rabatt) darf GAR NICHT persistiert
        // werden — sonst widerspricht die gespeicherte Kalkulation (impliziert EP 0/negativ)
        // dem unverändert stehenbleibenden ALTEN position.preis. Dieselbe Ablehnung, die
        // position-kalkulation-modal.tsx bereits über ein deaktiviertes Save-Button erzwingt,
        // greift hier VOR dem Persistieren, für ALLE Aufrufer dieser Store-Funktion. Siehe
        // shared/kalkulation.ts::darfKalkulationPersistiertWerden für die volle Begründung.
        //
        // P1 (Fix, Codex-Fund): dieser Ablehnungs-Zweig kehrte bisher STILL zurück (kein
        // Rückgabewert) — Aufrufer (catalog-suggest-dialog.tsx, catalog-batch-suggest*.ts(x),
        // X84PriceImport.tsx, anwendenGesamtaufschlag in blitzkalkulation-view.tsx) konnten das
        // nicht von einem erfolgreichen Update unterscheiden und meldeten fälschlich "übernommen"
        // bzw. leerten ihr Formular, obwohl NICHTS gespeichert wurde. Fix: boolisches
        // Erfolgssignal — ALLE Aufrufer wurden durchgesehen und prüfen es jetzt.
        if (!darfKalkulationPersistiertWerden(normalizedKalkulation, kalkulationsDefaults)) {
          return false;
        }

        // Einheitspreis nur neu setzen, wenn die Kalkulation tatsächlich Werte enthält;
        // beim Entfernen der Kalkulation bleibt der letzte Preis stehen.
        // P2-5 (Fix, Regression aus P1-B): hatKalkulation() lässt jetzt auch eine reine
        // negative Zeile (z.B. ein Rabatt ohne zugehörige Kostenzeile) als "befüllt" gelten
        // (richtig für die Import-Konflikt-Erkennung) — das würde hier aber einen negativen
        // Einzelpreis ableiten und speichern. Die EP-Ableitung MUSS stattdessen prüfen, ob die
        // Kalkulation eine POSITIVE Netto-Summe ergibt (istKalkulationGueltigFuerEpAbleitung).
        let priceNumericValue: number | undefined;
        if (istKalkulationGueltigFuerEpAbleitung(normalizedKalkulation, kalkulationsDefaults)) {
          const menge = quoteItemIndex !== -1 ? toSafeNumber(quoteItems[quoteItemIndex].menge, 1) : 1;
          priceNumericValue = einheitspreisAusKalkulation(normalizedKalkulation, menge, kalkulationsDefaults);
        }

        const updatedPosition: Position = {
          ...currentPosition,
          kalkulation: normalizedKalkulation,
        };
        if (priceNumericValue !== undefined) {
          updatedPosition.preis = priceNumericValue.toFixed(2) as unknown as Position['preis'];
        }

        const newPositions = [...positions];
        newPositions[positionIndex] = updatedPosition;

        let newQuoteItems = quoteItems;
        if (quoteItemIndex !== -1) {
          const updatedQuoteItem = { ...quoteItems[quoteItemIndex] };
          updatedQuoteItem.kalkulation = normalizedKalkulation ?? undefined;
          if (priceNumericValue !== undefined) {
            let newItemPrice = priceNumericValue;
            if (discountApplicationMode === 'distributed' && discountAmount > 0 && discountType === 'percent') {
              newItemPrice = parseFloat((priceNumericValue * (1 - discountAmount / 100)).toFixed(2));
            }
            updatedQuoteItem.preis = newItemPrice;
          }
          newQuoteItems = [...quoteItems];
          newQuoteItems[quoteItemIndex] = updatedQuoteItem;
        }

        let newOriginalPrices = originalPrices;
        if (priceNumericValue !== undefined) {
          newOriginalPrices = { ...originalPrices, [id]: priceNumericValue };
        }

        const historyEntry: PositionEditHistoryEntry = {
          positionId: id,
          previousPosition,
          previousQuoteItem,
          previousOriginalPrice,
        };
        const newHistory = [...editHistory, historyEntry].slice(-20);

        set({
          positions: newPositions,
          quoteItems: newQuoteItems,
          originalPrices: newOriginalPrices,
          editHistory: newHistory,
        });
        return true;
      },

      setKalkulationsDefaults: (defaults) => {
        const current = get().kalkulationsDefaults;
        const next: KalkulationsDefaults = {
          stundensatz:
            defaults.stundensatz !== undefined && Number.isFinite(defaults.stundensatz) && defaults.stundensatz >= 0
              ? defaults.stundensatz
              : current.stundensatz,
          materialAufschlagProzent:
            defaults.materialAufschlagProzent !== undefined &&
            Number.isFinite(defaults.materialAufschlagProzent) &&
            defaults.materialAufschlagProzent >= 0
              ? defaults.materialAufschlagProzent
              : current.materialAufschlagProzent,
        };
        set({ kalkulationsDefaults: next });
      },

      setKalkulationsView: (view) => {
        set({ kalkulationsView: view });
      },

      updatePositionAufmass: (id, aufmass) => {
        const { positions, quoteItems } = get();
        const positionIndex = positions.findIndex((p) => p.id === id);
        if (positionIndex === -1) return;

        const normalized = aufmass && aufmass.zeilen.length > 0 ? aufmass : null;

        const newPositions = [...positions];
        newPositions[positionIndex] = { ...positions[positionIndex], aufmass: normalized };

        let newQuoteItems = quoteItems;
        const quoteItemIndex = quoteItems.findIndex((item) => item.id === id);
        if (quoteItemIndex !== -1) {
          newQuoteItems = [...quoteItems];
          newQuoteItems[quoteItemIndex] = { ...quoteItems[quoteItemIndex], aufmass: normalized ?? undefined };
        }

        set({ positions: newPositions, quoteItems: newQuoteItems });
      },

      setAufmassView: (open) => {
        set({ aufmassView: open });
      },

      updateQuoteHeader: (key, value) => {
        if (key === 'mwstSatz') {
          get().setGlobalVatRate(toSafeNumber(value, 19));
          return;
        }

        set({
          quoteHeader: {
            ...get().quoteHeader,
            [key]: String(value ?? ''),
          },
        });
      },
      
      setQuoteHeader: (header) =>
        set({
          quoteHeader: {
            ...DEFAULT_HEADER,
            ...header,
            mwstSatz: normalizeVatRate((header as any).mwstSatz),
          },
        }),
      
      setQuoteItems: (items) => {
        const vatRate = normalizeVatRate(get().quoteHeader.mwstSatz);
        set({
          quoteItems: items.map((item) => ({
            ...item,
            umsatzsteuer: vatRate,
          })),
        });
      },

      reorderPositions: (fromId, toId) => {
        if (fromId === toId) {
          return;
        }

        const { positions, quoteItems } = get();
        const fromIndex = positions.findIndex((position) => position.id === fromId);
        const toIndex = positions.findIndex((position) => position.id === toId);

        if (fromIndex < 0 || toIndex < 0) {
          return;
        }

        const reorderedPositions = [...positions];
        const [movedPosition] = reorderedPositions.splice(fromIndex, 1);
        reorderedPositions.splice(toIndex, 0, movedPosition);

        set({
          positions: reorderedPositions,
          initialPositionsOrder: [...reorderedPositions],
          quoteItems: sortQuoteItemsByPositionsOrder(quoteItems, reorderedPositions),
        });
        // Reihenfolge ist Struktur: im Auto-Modus OZ nachziehen.
        get().renumberPositions({ onlyIfAuto: true });
      },

      renumberPositions: (options) => {
        const { positions, quoteItems, gewerkOrder, numberingMode, isGAEBDatabase } = get();
        if (options?.onlyIfAuto && numberingMode !== 'auto') return;
        // GAEB-Welt bleibt unangetastet (eigene OZ-Masken; Konsolidierung = TODO F3).
        if (isGAEBDatabase) return;

        const result = applyQuoteNumbering(positions, quoteItems, gewerkOrder);
        set({
          positions: result.positions,
          // initialPositionsOrder spiegeln, sonst stellt resetSearch alte OZ wieder her.
          initialPositionsOrder: [...result.positions],
          quoteItems: result.quoteItems,
          // Expliziter Klick aktiviert den Auto-Modus (persistiert via editorState).
          numberingMode: 'auto',
        });
      },

      removePosition: (id) => {
        const { positions, quoteItems, originalPrices, editHistory, initialPositionsOrder } = get();
        const nextPositions = positions.filter((position) => position.id !== id);
        const nextQuoteItems = quoteItems.filter((item) => item.id !== id);
        const nextOriginalPrices = { ...originalPrices };
        delete nextOriginalPrices[id];
        const nextInitialPositionsOrder = initialPositionsOrder.filter((position) => position.id !== id);

        set({
          positions: nextPositions,
          initialPositionsOrder: nextInitialPositionsOrder,
          quoteItems: nextQuoteItems,
          originalPrices: nextOriginalPrices,
          editHistory: editHistory.filter((entry) => entry.positionId !== id),
        });
        // Löschen in der Mitte: im Auto-Modus Lücke schließen (1.2 rückt nach).
        get().renumberPositions({ onlyIfAuto: true });
      },

      updateItemDiscount: (id, rabattWert, rabattTyp) => {
        const safeValue = Math.max(0, Math.round(rabattWert * 100) / 100);
        const { quoteItems } = get();
        const updatedItems = quoteItems.map((item) =>
          item.id === id
            ? {
                ...item,
                rabattTyp,
                rabattWert: safeValue,
              }
            : item,
        );
        set({ quoteItems: updatedItems });      },

      clearItemDiscount: (id) => {
        const { quoteItems } = get();
        const updatedItems = quoteItems.map((item) =>
          item.id === id
            ? {
                ...item,
                rabattTyp: undefined,
                rabattWert: undefined,
              }
            : item,
        );
        set({ quoteItems: updatedItems });      },

      appendPosition: (position, quoteItem) => {
        const { positions, quoteItems, initialPositionsOrder, gewerkOrder } = get();
        searchCache.clearCache();
        // Neues Gewerk hinten an die BESTEHENDE Reihenfolge anhängen — setPositions
        // würde gewerkOrder aus der Positions-Sequenz neu bauen und damit eine vom
        // Nutzer umsortierte Gewerk-Reihenfolge verwerfen (inkl. OZ-Verschiebung).
        const gewerkName = (position.gewerk || '').trim();
        const nextGewerkOrder = gewerkName && !gewerkOrder.includes(gewerkName)
          ? [...gewerkOrder, gewerkName]
          : gewerkOrder;
        set({
          positions: [...positions, position],
          initialPositionsOrder: [...(initialPositionsOrder || []), position],
          quoteItems: [...quoteItems, quoteItem],
          gewerkOrder: nextGewerkOrder,
          // Eine angehängte Position IST ein geladenes Angebot. Ohne dieses
          // Flag rendert Home das Dashboard statt des Editors — der IFC-Import
          // (Dashboard → /ifc-import → Übernahme → navigate('/')) ließ die
          // übernommenen Positionen dadurch unsichtbar im Store liegen
          // (E2E-Fund e2e/ifc-import.spec.ts; der Excel-Weg setzt das Flag
          // über setPositions und war nie betroffen).
          isDataLoaded: true,
        });
        // Einfügen ist eine Strukturänderung: im Auto-Modus OZ direkt vergeben.
        get().renumberPositions({ onlyIfAuto: true });
      },

      setItemPositionType: (id, type) => {
        const { quoteItems, positions, initialPositionsOrder } = get();
        const isBedarfsposition = type === 'bedarf';
        const isAlternativposition = type === 'alternativ';
        const updatedItems = quoteItems.map((item) =>
          item.id === id ? { ...item, isBedarfsposition, isAlternativposition } : item,
        );
        // Auch auf der Position spiegeln, damit GAEB-Export (liest entry.isBedarfsposition)
        // und Persistenz konsistent sind.
        const applyFlags = (position: any) =>
          position.id === id
            ? { ...position, isBedarfsposition, isAlternativposition }
            : position;
        const updatedPositions = positions.map(applyFlags);
        // initialPositionsOrder mitführen — resetSearch() stellt positions daraus wieder
        // her; ohne Update ginge die Klassifizierung beim Löschen der Suche verloren.
        const updatedInitialOrder = (initialPositionsOrder || []).map(applyFlags);
        set({
          quoteItems: updatedItems,
          positions: updatedPositions,
          initialPositionsOrder: updatedInitialOrder,
        });
      },

      setGlobalVatRate: (rate) => {
        const normalizedRate = normalizeVatRate(rate);
        const { positions, quoteItems, quoteHeader } = get();

        set({
          quoteHeader: {
            ...quoteHeader,
            mwstSatz: normalizedRate,
          },
          positions: positions.map((position) => ({
            ...position,
            umsatzsteuer: normalizedRate,
          })),
          quoteItems: quoteItems.map((item) => ({
            ...item,
            umsatzsteuer: normalizedRate,
          })),
        });
      },
      
      setGewerkOrder: (order) =>
        set({ gewerkOrder: order }),

      updateGewerkOrder: (newOrder) => {
        set({ gewerkOrder: newOrder });
        // Titel-Reihenfolge ist Struktur: im Auto-Modus OZ nachziehen.
        get().renumberPositions({ onlyIfAuto: true });
      },

      reorderGewerk: (fromIndex, toIndex) => {
        const currentOrder = [...get().gewerkOrder];
        if (fromIndex === toIndex) return; // No change needed

        // Remove the item from its original position
        const [movedItem] = currentOrder.splice(fromIndex, 1);

        // Insert the item at the new position
        currentOrder.splice(toIndex, 0, movedItem);

        // Update the state with new order
        set({ gewerkOrder: currentOrder });
        // Titel-Reihenfolge ist Struktur: im Auto-Modus OZ nachziehen.
        get().renumberPositions({ onlyIfAuto: true });
      },
      
      resetSearch: () => {
        const { initialPositionsOrder } = get();
        set({ 
          searchTerm: '', 
          nameSearchTerm: '', 
          currentPage: 1, 
          sortField: null, 
          sortDirection: 'asc',
          positions: [...initialPositionsOrder] // Reset to the original order
        });
      },
      
      setLeftPaneWidth: (width) => {
        set({ leftPaneWidth: width });
        try { localStorage.setItem('offer-leftPaneWidth', width); } catch {}
      },

      setCurrentQuotePersistence: (identity) =>
        set({
          currentQuoteSlot: identity?.slot ?? null,
          currentQuoteStorageRevision: identity?.storageRevision ?? null,
        }),
      
      setIsVoiceQuote: (isVoice) =>
        set({ isVoiceQuote: isVoice }),

      undoLastEdit: () => {
        const { editHistory, positions, quoteItems, originalPrices, initialPositionsOrder } = get();
        if (editHistory.length === 0) {
          return;
        }

        const lastEdit = editHistory[editHistory.length - 1];

        const restoredPositions = positions.map((position) =>
          position.id === lastEdit.positionId ? { ...lastEdit.previousPosition } : position
        );

        // Auch den resetSearch-Mirror wiederherstellen — sonst aufersteht der
        // rückgängig gemachte Edit beim nächsten "Suche zurücksetzen" (Codex-P2).
        const restoredInitialOrder = (initialPositionsOrder || []).map((position) =>
          position.id === lastEdit.positionId ? { ...lastEdit.previousPosition } : position
        );

        let restoredQuoteItems = quoteItems;
        if (lastEdit.previousQuoteItem) {
          const quoteIndex = quoteItems.findIndex((item) => item.id === lastEdit.positionId);
          if (quoteIndex !== -1) {
            restoredQuoteItems = [...quoteItems];
            restoredQuoteItems[quoteIndex] = { ...lastEdit.previousQuoteItem };
          }
        } else {
          // Kein previousQuoteItem im History-Eintrag, aber INZWISCHEN existiert eines
          // (Zeile wurde nach dem Edit aktiviert, menge 0→>0): die Strukturfelder aus
          // der wiederhergestellten Position übernehmen — sonst exportiert das Item
          // weiter das rückgängig gemachte Gewerk/Untertitel (Codex-P2).
          const quoteIndex = quoteItems.findIndex((item) => item.id === lastEdit.positionId);
          if (quoteIndex !== -1) {
            const restored = lastEdit.previousPosition as any;
            restoredQuoteItems = [...quoteItems];
            restoredQuoteItems[quoteIndex] = {
              ...quoteItems[quoteIndex],
              gewerk: restored.gewerk || undefined,
              untergewerkName: restored.untergewerkName || undefined,
              name: restored.name || undefined,
              beschreibung: restored.beschreibung,
              einheit: restored.einheit,
              positionNumber: restored.positionNumber || undefined,
            };
          }
        }

        const restoredOriginalPrices = { ...originalPrices };
        if (lastEdit.previousOriginalPrice !== undefined) {
          restoredOriginalPrices[lastEdit.positionId] = lastEdit.previousOriginalPrice;
        } else {
          delete restoredOriginalPrices[lastEdit.positionId];
        }

        set({
          positions: restoredPositions,
          initialPositionsOrder: restoredInitialOrder,
          quoteItems: restoredQuoteItems,
          originalPrices: restoredOriginalPrices,
          editHistory: editHistory.slice(0, -1),
        });

        // Im Auto-Modus renumbern Struktur-Edits MEHRERE Zeilen (Lauf-Join, Titelwechsel);
        // die Einzel-Zeilen-Wiederherstellung ließe stale/doppelte OZ zurück (Codex-P2).
        // Neu nummerieren stellt die Konsistenz für ALLE Zeilen wieder her.
        get().renumberPositions({ onlyIfAuto: true });
      },

      // Discount actions
      setDiscount: (amount, type, applyToPrices = true) => {
        const { discountApplicationMode, quoteItems, originalPrices, isDiscountAppliedToPrices } = get();
        
        // If trying to use absolute discount in distributed mode, switch to total mode
        if (discountApplicationMode === 'distributed' && type === 'absolute') {
          console.warn('Absolute discounts are only supported in "total" mode. Switching to total mode.');
          
          // Reset prices to original before switching
          const updatedQuoteItems = quoteItems.map(item => ({
            ...item,
            preis: originalPrices[item.id] || item.preis
          }));
          
          set({ 
            discountApplicationMode: 'total', 
            discountAmount: amount, 
            discountType: type,
            isDiscountAppliedToPrices: false,
            quoteItems: updatedQuoteItems
          });

          return;
        }
        
        // Store original prices if not already stored
        const newOriginalPrices = { ...originalPrices };
        if (!isDiscountAppliedToPrices && Object.keys(newOriginalPrices).length === 0) {
          quoteItems.forEach(item => {
            const price = typeof item.preis === 'string' ? parseFloat(item.preis) : item.preis;
            newOriginalPrices[item.id] = price;
          });
        }
        
        // If applyToPrices is false, just store the amount without recalculating prices
        // This is used when typing in the input field to avoid expensive recalculations
        if (!applyToPrices) {
          set({ 
            discountAmount: amount, 
            discountType: type,
            originalPrices: newOriginalPrices
          });
          return;
        }
        
        // Apply discount to individual prices if mode is 'distributed' (percentage only)
        let updatedQuoteItems = quoteItems;
        if (discountApplicationMode === 'distributed' && amount > 0 && type === 'percent') {
          updatedQuoteItems = quoteItems.map(item => {
            const originalPrice = newOriginalPrices[item.id] || (typeof item.preis === 'string' ? parseFloat(item.preis) : item.preis);
            const newPrice = originalPrice * (1 - amount / 100);
            
            return {
              ...item,
              preis: Math.max(0, parseFloat(newPrice.toFixed(2)))
            };
          });
        } else if (discountApplicationMode === 'distributed' && amount === 0) {
          // Reset to original prices
          updatedQuoteItems = quoteItems.map(item => ({
            ...item,
            preis: newOriginalPrices[item.id] || item.preis
          }));
        }
        
        set({ 
          discountAmount: amount, 
          discountType: type,
          originalPrices: newOriginalPrices,
          isDiscountAppliedToPrices: discountApplicationMode === 'distributed' && amount > 0 && type === 'percent',
          quoteItems: updatedQuoteItems
        });
        
      },
      
      // Apply distributed discount to all Einzelpreise
      applyDistributedDiscount: () => {
        const { discountAmount, discountType, quoteItems, originalPrices, isDiscountAppliedToPrices } = get();
        
        // Only apply if discount is percent and there's a discount amount
        if (discountType !== 'percent' || discountAmount <= 0) {
          console.warn('Distributed discount only works with percentage discounts > 0');
          return;
        }
        
        // Don't re-apply if already applied
        if (isDiscountAppliedToPrices) {
          console.warn('Discount already applied to prices');
          return;
        }
        
        // Store original prices if not already stored
        const newOriginalPrices = { ...originalPrices };
        if (Object.keys(newOriginalPrices).length === 0) {
          quoteItems.forEach(item => {
            const price = typeof item.preis === 'string' ? parseFloat(item.preis) : item.preis;
            newOriginalPrices[item.id] = price;
          });
        }
        
        // Apply discount to all prices
        const updatedQuoteItems = quoteItems.map(item => {
          const originalPrice = newOriginalPrices[item.id] || (typeof item.preis === 'string' ? parseFloat(item.preis) : item.preis);
          const newPrice = originalPrice * (1 - discountAmount / 100);
          
          return {
            ...item,
            preis: Math.max(0, parseFloat(newPrice.toFixed(2)))
          };
        });
        
        set({ 
          discountApplicationMode: 'distributed',
          originalPrices: newOriginalPrices,
          isDiscountAppliedToPrices: true,
          quoteItems: updatedQuoteItems
        });
        
      },
      
      setDiscountApplicationMode: (mode) => {
        const { discountAmount, discountType, quoteItems, originalPrices } = get();
        
        // If switching to 'distributed' with absolute discount, switch to percent discount or warn
        if (mode === 'distributed' && discountType === 'absolute') {
          console.warn('Absolute discounts are not supported in "distributed" mode. Please use percentage discounts.');
          return; // Don't switch mode
        }
        
        // If switching to 'distributed', apply discount to prices (percentage only)
        if (mode === 'distributed' && discountAmount > 0 && discountType === 'percent') {
          // Store original prices if not stored yet
          const newOriginalPrices = { ...originalPrices };
          if (Object.keys(newOriginalPrices).length === 0) {
            quoteItems.forEach(item => {
              const price = typeof item.preis === 'string' ? parseFloat(item.preis) : item.preis;
              newOriginalPrices[item.id] = price;
            });
          }
          
          // Apply percentage discount to each item
          const updatedQuoteItems = quoteItems.map(item => {
            const originalPrice = newOriginalPrices[item.id] || (typeof item.preis === 'string' ? parseFloat(item.preis) : item.preis);
            const newPrice = originalPrice * (1 - discountAmount / 100);
            
            return {
              ...item,
              preis: Math.max(0, parseFloat(newPrice.toFixed(2)))
            };
          });
          
          set({ 
            discountApplicationMode: mode,
            originalPrices: newOriginalPrices,
            isDiscountAppliedToPrices: true,
            quoteItems: updatedQuoteItems
          });
        } else if (mode === 'total') {
          // Reset to original prices when switching to 'total'
          const updatedQuoteItems = quoteItems.map(item => ({
            ...item,
            preis: originalPrices[item.id] || item.preis
          }));
          
          set({ 
            discountApplicationMode: mode,
            isDiscountAppliedToPrices: false,
            quoteItems: updatedQuoteItems
          });
        } else {
          set({ discountApplicationMode: mode });
        }
        
      },
      
      clearDiscount: () => {
        const { quoteItems, originalPrices } = get();
        
        // Reset to original prices
        const updatedQuoteItems = quoteItems.map(item => ({
          ...item,
          preis: originalPrices[item.id] || item.preis
        }));
        
        set({ 
          discountAmount: 0, 
          discountType: 'percent', 
          discountApplicationMode: 'total',
          originalPrices: {},
          isDiscountAppliedToPrices: false,
          quoteItems: updatedQuoteItems
        });
        
      },

      // GAEB actions
      updateGAEBEntry: (id, updates) => set((state) => ({
        positions: state.positions.map((pos) => {
          if (pos.id !== id) {
            return pos;
          }

          const merged = { ...pos, ...updates } as typeof pos;

          if ('type' in merged) {
            const gaebMerged = merged as typeof pos & import('../../../shared/types').GAEBEntry;
            const nextLongText = normalizeGAEBLongText((updates as any).longText ?? gaebMerged.longText);
            const nextShortText = ((updates as any).shortText ?? gaebMerged.shortText ?? gaebMerged.name ?? gaebMerged.beschreibung) as string;

            // CRITICAL: Handle ourComment explicitly (same pattern as adjustedQuantity)
            // If updates has ourComment key (even undefined), use that value
            // This ensures we can clear comments by setting to undefined
            const hasOurCommentUpdate = 'ourComment' in updates;
            const nextComment = hasOurCommentUpdate
              ? (updates as any).ourComment
              : gaebMerged.ourComment;
            const requestedOurPrice = (updates as any).ourPrice;
            const fallbackPrice = (() => {
              if (typeof gaebMerged.preis === 'number') {
                return gaebMerged.preis;
              }
              const parsed = parseFloat(String(gaebMerged.preis ?? '0').replace(',', '.'));
              return Number.isFinite(parsed) ? parsed : 0;
            })();
            const resolvedPrice = typeof requestedOurPrice === 'number'
              ? requestedOurPrice
              : (typeof gaebMerged.ourPrice === 'number'
                  ? gaebMerged.ourPrice
                  : (typeof gaebMerged.originalPrice === 'number'
                      ? gaebMerged.originalPrice
                      : fallbackPrice));

            const recomposedDescription = composeGAEBDescription({
              shortText: nextShortText,
              longText: nextLongText,
              ourComment: nextComment,
            }) || gaebMerged.beschreibung;

            // CRITICAL: Handle adjustedQuantity explicitly
            // If updates has adjustedQuantity key (even undefined), use that value
            // This ensures we can reset adjustedQuantity to undefined (back to original)
            const hasAdjustedQuantityUpdate = 'adjustedQuantity' in updates;
            const nextAdjustedQuantity = hasAdjustedQuantityUpdate
              ? (updates as any).adjustedQuantity
              : gaebMerged.adjustedQuantity;

            console.log('[STORE updateGAEBEntry]', {
              id,
              hasAdjustedQuantityUpdate,
              updatesAdjustedQuantity: (updates as any).adjustedQuantity,
              previousAdjustedQuantity: gaebMerged.adjustedQuantity,
              nextAdjustedQuantity
            });

            return {
              ...gaebMerged,
              name: nextShortText,
              shortText: nextShortText,
              longText: nextLongText,
              ourComment: nextComment,
              beschreibung: recomposedDescription,
              preis: Number.isFinite(resolvedPrice) ? Number(resolvedPrice.toFixed(2)) : Number(fallbackPrice.toFixed(2)),
              ourPrice: typeof requestedOurPrice === 'number' ? requestedOurPrice : gaebMerged.ourPrice,
              adjustedQuantity: nextAdjustedQuantity,
            } as typeof pos;
          }

          return {
            ...merged,
            preis: Number(merged.preis) || 0,
          } as typeof pos;
        }),
      })),

      updateGAEBMetadata: (updates) => set((state) => ({
        gaebMetadata: state.gaebMetadata ? { ...state.gaebMetadata, ...updates } : null
      })),

      convertGAEBToOffer: () => {
        const { positions, gaebMetadata, isGAEBDatabase } = get();

        if (!isGAEBDatabase) {
          console.warn('Cannot convert: not a GAEB database');
          return;
        }

        // Convert GAEB entries to quote items
        const quoteItems: QuoteItem[] = positions
          .map(pos => {
            const gaebEntry = pos as any; // Cast to GAEBEntry

            // Only convert actual items, not titles
            if (gaebEntry.type !== 'item') {
              return null;
            }

            // Use intelligent price logic: ourPrice if explicitly set (even 0!), otherwise originalPrice
            // Note: 0€ is a valid price (e.g., included services), so we check for undefined, not <= 0
            const hasOurPrice = typeof gaebEntry.ourPrice === 'number';
            const hasOriginalPrice = typeof gaebEntry.originalPrice === 'number';
            const price = hasOurPrice ? gaebEntry.ourPrice : (hasOriginalPrice ? gaebEntry.originalPrice : undefined);

            // Use adjusted quantity if exists, otherwise original quantity
            const quantity = gaebEntry.adjustedQuantity ?? gaebEntry.originalQuantity ?? gaebEntry.menge ?? 0;

            // Skip items that have NO price at all (not even 0€)
            // 0€ is valid (included services), but undefined means "not priced"
            if (price === undefined) {
              return null;
            }

            const normalizedLongText = normalizeGAEBLongText(gaebEntry.longText);
            const description = composeGAEBDescription({
              shortText: gaebEntry.shortText || pos.name || pos.beschreibung || '',
              longText: normalizedLongText,
              ourComment: gaebEntry.ourComment,
            }) || (pos.beschreibung || '');

            return {
              id: pos.id,
              originalId: (gaebEntry.artikelId || gaebEntry.positionNumber || String(pos.id)),
              positionNumber: gaebEntry.positionNumber || undefined, // Variable depth LV-Nr
              name: gaebEntry.shortText || pos.name || '',
              beschreibung: description,
              gewerk: pos.gewerk || undefined,
              einheit: pos.einheit,
              preis: Number(price),
              umsatzsteuer: Number(pos.umsatzsteuer || 19),
              menge: Number(quantity),
              budgetTaskId: (gaebEntry as any).budgetTaskId || (gaebEntry as any).taskId || (pos as any).budgetTaskId || undefined,
              isBedarfsposition: gaebEntry.isBedarfsposition || false,
              isAlternativposition: gaebEntry.isAlternativposition || false,
            } as QuoteItem;
          })
          .filter((item): item is QuoteItem => item !== null);

        // Update quote header with GAEB metadata
        const newHeader: QuoteHeader = {
          kunde: gaebMetadata?.clientName || '',
          anschrift: '',
          angebot_nr: '', // kommt fortlaufend aus dem Nummernkreis (siehe unten)
          datum: new Date().toISOString().split('T')[0],
          bauvorhaben: gaebMetadata?.projectName || '',
          gewerk: '',
          auftrag: '',
          eingereichte_unterlagen: '',
          lz: '',
          mwstSatz: 19,
        };

        // Extract unique Gewerke and set order
        const uniqueGewerke = Array.from(new Set(quoteItems.map(item => item.gewerk || 'Allgemein')));

        set((state) => ({
          quoteItems,
          quoteHeader: newHeader,
          gewerkOrder: uniqueGewerke,
          isGAEBDatabase: false, // Switch to regular offer mode
          gaebMetadata: null,
          currentDatabaseId: null,
          currentQuoteSlot: null,
          currentQuoteStorageRevision: null,
          // Bestandsschutz: das konvertierte Angebot trägt importierte GAEB-OZ —
          // ein klebender Auto-Modus dürfte sie nicht still renumbern.
          numberingMode: 'manual',
          offerDocumentEpoch: state.offerDocumentEpoch + 1,
        }));

        scheduleQuoteNumberAllocation(get, set);

        console.log(`Converted ${quoteItems.length} GAEB items to offer`);

      },

      clearAll: () => {
        // Clear search cache when resetting the app
        searchCache.clearCache();
        set((state) => ({
          positions: [], 
          initialPositionsOrder: [], // Reset initial order too
          quoteItems: [], 
          searchTerm: '', 
          nameSearchTerm: '', // Reset name search too
          currentPage: 1,
          sortField: null,
          sortDirection: 'asc',
          quoteHeader: { ...DEFAULT_HEADER },
          gewerkOrder: [], // Reset Gewerk order
          isDataLoaded: false,
          currentQuoteSlot: null, // Reset quote tracking
          currentQuoteStorageRevision: null,
          isVoiceQuote: false, // Reset voice tracking
          isGAEBDatabase: false, // Reset GAEB tracking
          gaebMetadata: null, // Reset GAEB metadata
          currentDatabaseId: null, // Reset current database ID
          ...DEFAULT_PERSISTED_QUOTE_EDITOR_STATE,
          editHistory: [],
          offerDocumentEpoch: state.offerDocumentEpoch + 1,
        }));
      },

      initNewQuote: (options) => {
        searchCache.clearCache();
        const newHeader: QuoteHeader = {
          kunde: '',
          anschrift: '',
          angebot_nr: '', // kommt fortlaufend aus dem Nummernkreis (siehe unten)
          datum: new Date().toISOString().split('T')[0],
          mwstSatz: 19,
        };
        set((state) => ({
          positions: [],
          initialPositionsOrder: [],
          quoteItems: [],
          searchTerm: '',
          nameSearchTerm: '',
          currentPage: 1,
          sortField: null,
          sortDirection: 'asc',
          quoteHeader: newHeader,
          gewerkOrder: [],
          isDataLoaded: true, // Go straight to editor
          currentQuoteSlot: null,
          currentQuoteStorageRevision: null,
          isVoiceQuote: false,
          isGAEBDatabase: false,
          gaebMetadata: null,
          currentDatabaseId: null,
          ...DEFAULT_PERSISTED_QUOTE_EDITOR_STATE,
          editHistory: [],
          offerDocumentEpoch: state.offerDocumentEpoch + 1,
        }));

        // Nachtrags-Brücke (home.tsx) vergibt die Nummer selbst als <Basis>/N<n>
        // und schaltet die normale Kreis-Vergabe hier ab. Der Epoch-Dedup-Merker
        // wird trotzdem gesetzt, damit das Sidebar-Auffangnetz die Nachtrags-
        // Nummer nicht mit einer Kreis-Nummer überholt.
        if (options?.allocateNumber !== false) {
          scheduleQuoteNumberAllocation(get, set);
        } else {
          lastQuoteNumberAllocationEpoch = get().offerDocumentEpoch;
        }
      },

      ensureQuoteNumberAllocated: () => {
        scheduleQuoteNumberAllocation(get, set);
      },

      // Computed functions for sidebar
      getPositionsWithQuantities: () => get().quoteItems,
      getTotalsByGewerk: () => {
        const { quoteItems, quoteHeader } = get();
        const vatRate = normalizeVatRate(quoteHeader.mwstSatz);
        const gewerkGroups = quoteItems.reduce((acc, item) => {
          if (isExcludedFromMainSum(item)) return acc; // Bedarf/Alternativ nicht in Hauptsumme
          const gewerk = item.gewerk?.trim() || 'Allgemein';

          if (!acc[gewerk]) {
            acc[gewerk] = { total: 0, count: 0 };
          }

          const netAmount = getLineNetAfterDiscount(item);
          const taxAmount = (netAmount * vatRate) / 100;
          const grossAmount = netAmount + taxAmount;

          acc[gewerk].total += grossAmount;
          acc[gewerk].count += 1;

          return acc;
        }, {} as Record<string, { total: number; count: number }>);

        return Object.entries(gewerkGroups).map(([gewerk, data]) => ({
          gewerk,
          total: data.total,
          count: data.count,
        }));
      },
      getGrandTotal: () => {
        const { quoteItems, quoteHeader } = get();
        const vatRate = normalizeVatRate(quoteHeader.mwstSatz);

        let totalNet = 0;
        let totalTax = 0;

        quoteItems.forEach((item) => {
          if (isExcludedFromMainSum(item)) return; // Bedarf/Alternativ nicht in Hauptsumme
          const netAmount = getLineNetAfterDiscount(item);
          const taxAmount = (netAmount * vatRate) / 100;

          totalNet += netAmount;
          totalTax += taxAmount;
        });

        return {
          net: totalNet,
          tax: totalTax,
          gross: totalNet + totalTax,
        };
      }
    })
);

// Filter and sort positions
// Memoized search function with cache
const createSearchCache = () => {
  let cache: Record<string, Position[]> = {};
  let lastResult: { results: Position[], total: number, totalPages: number, currentPage: number } | null = null;
  
  return {
    // Get cached results if parameters haven't changed
    getFilteredPositions: (
      positions: Position[],
      searchTerm: string,
      nameSearchTerm: string,
      sortField: keyof Position | null,
      sortDirection: 'asc' | 'desc',
      currentPage: number,
      pageSize: number
    ) => {
      // Create a cache key based on search parameters
      const cacheKey = `${searchTerm}|${nameSearchTerm}|${String(sortField)}|${sortDirection}`;
      
      // Try to use cached filtered results
      let filtered: Position[] = cache[cacheKey];
      
      // If not cached, filter and sort
      if (!filtered) {
        // Filter by search terms
        if (!searchTerm && !nameSearchTerm) {
          filtered = positions;
        } else {
          filtered = positions.filter(pos => {
            // General search (if provided)
            const generalMatch = !searchTerm || (() => {
          const searchLower = searchTerm.toLowerCase();
              return (
            (pos.originalId && pos.originalId.toLowerCase().includes(searchLower)) ||
            (pos.gewerk && pos.gewerk.toLowerCase().includes(searchLower)) ||
                (pos.name && pos.name.toLowerCase().includes(searchLower)) ||
            pos.beschreibung.toLowerCase().includes(searchLower) ||
            pos.einheit.toLowerCase().includes(searchLower) ||
            String(pos.preis).includes(searchTerm) ||
            String(pos.umsatzsteuer).includes(searchTerm)
              );
            })();
            
            // Name-specific search (if provided)
            const nameMatch = !nameSearchTerm || (
              pos.name && pos.name.toLowerCase().includes(nameSearchTerm.toLowerCase())
            );
            
            // Both conditions must be true
            return generalMatch && nameMatch;
          });
        }
        
        // Sort results
        if (sortField) {
          filtered.sort((a, b) => {
            const aVal = a[sortField];
            const bVal = b[sortField];
            
            if (typeof aVal === 'string' && typeof bVal === 'string') {
              return sortDirection === 'asc' 
                ? aVal.localeCompare(bVal)
                : bVal.localeCompare(aVal);
            } else {
              return sortDirection === 'asc'
                ? Number(aVal) - Number(bVal)
                : Number(bVal) - Number(aVal);
            }
          });
        }
        
        // Store in cache
        cache[cacheKey] = filtered;
        
        // Limit cache size
        const cacheKeys = Object.keys(cache);
        if (cacheKeys.length > 10) {
          delete cache[cacheKeys[0]];
        }
      }
      
      // Paginate
      const startIndex = (currentPage - 1) * pageSize;
      const paginatedResults = filtered.slice(startIndex, startIndex + pageSize);
      const totalPages = Math.ceil(filtered.length / pageSize);
      
      lastResult = {
        results: paginatedResults,
        total: filtered.length,
        totalPages,
        currentPage,
      };
      
      return lastResult;
    },
    
    // Clear search cache
    clearCache: () => {
      cache = {};
      lastResult = null;
    },
    
    // Get last result
    getLastResult: () => lastResult
  };
};

// Create a single instance of the search cache
const searchCache = createSearchCache();

export const useFilteredPositions = () => {
  const positions = useOfferStore(s => s.positions);
  const searchTerm = useOfferStore(s => s.searchTerm);
  const nameSearchTerm = useOfferStore(s => s.nameSearchTerm);
  const sortField = useOfferStore(s => s.sortField);
  const sortDirection = useOfferStore(s => s.sortDirection);
  const currentPage = useOfferStore(s => s.currentPage);
  const pageSize = useOfferStore(s => s.pageSize);
  
  // Use cached search function
  return searchCache.getFilteredPositions(
    positions,
    searchTerm,
    nameSearchTerm,
    sortField,
    sortDirection,
    currentPage,
    pageSize
  );
};

// Calculate quote totals with group subtotals by Gewerk
export const useQuoteTotals = () => {
  const quoteItems = useOfferStore(s => s.quoteItems);
  const gewerkOrder = useOfferStore(s => s.gewerkOrder);
  const quoteHeader = useOfferStore(s => s.quoteHeader);
  const vatRate = normalizeVatRate(quoteHeader.mwstSatz);
  
  // Group by tax rate (single global VAT rate, selectable in UI). Bedarfs-/
  // Alternativpositionen zählen NICHT in die Hauptsumme (nur nachrichtlich, s.u.).
  const taxGroups = quoteItems.reduce((acc, item) => {
    if (isExcludedFromMainSum(item)) return acc;
    if (!acc[vatRate]) {
      acc[vatRate] = { net: 0, tax: 0 };
    }

    const netAmount = getLineNetAfterDiscount(item);
    const taxAmount = netAmount * (vatRate / 100);

    acc[vatRate].net += netAmount;
    acc[vatRate].tax += taxAmount;

    return acc;
  }, {} as Record<number, { net: number; tax: number }>);
  
  // Group by Gewerk (trade/category)
  const gewerkGroups = quoteItems.reduce((acc, item) => {
    // Use a default category name if Gewerk is empty
    const gewerk = item.gewerk?.trim() || 'Allgemein';
    
    if (!acc[gewerk]) {
      acc[gewerk] = {
        name: gewerk,
        items: [],
        netTotal: 0,
        taxTotal: 0,
        grossTotal: 0
      };
    }
    
    // Bedarfs-/Alternativposition weiter im Gewerk ANZEIGEN, aber NICHT in die
    // Gewerk-Summe zählen (die Hauptsumme enthält sie nicht).
    const excluded = isExcludedFromMainSum(item);
    const netAmount = excluded ? 0 : getLineNetAfterDiscount(item);
    const taxAmount = netAmount * (vatRate / 100);
    const grossAmount = netAmount + taxAmount;

    // Add item to this group
    acc[gewerk].items.push(item);
    acc[gewerk].netTotal += netAmount;
    acc[gewerk].taxTotal += taxAmount;
    acc[gewerk].grossTotal += grossAmount;

    return acc;
  }, {} as Record<string, { 
    name: string, 
    items: QuoteItem[], 
    netTotal: number, 
    taxTotal: number, 
    grossTotal: number 
  }>);
  
  // Filter out header-only Gewerk groups (where all items have menge=0 and no price)
  // This ensures sidebar count matches the export preview count
  const gewerkGroupsArray = Object.values(gewerkGroups).filter(group =>
    group.items.some(item => {
      const menge = Number(item.menge) || 0;
      const preis = Number(item.preis) || 0;
      return menge > 0 || preis > 0;
    })
  );
  let sortedGewerkGroups;
  
  if (gewerkOrder && gewerkOrder.length > 0) {
    // Create a mapping of Gewerk names to their position in the custom order
    const orderMap = new Map(gewerkOrder.map((name, index) => [name, index]));
    
    // Sort according to the custom order
    sortedGewerkGroups = gewerkGroupsArray.sort((a, b) => {
      const aIndex = orderMap.has(a.name) ? orderMap.get(a.name)! : Number.MAX_SAFE_INTEGER;
      const bIndex = orderMap.has(b.name) ? orderMap.get(b.name)! : Number.MAX_SAFE_INTEGER;
      
      // If both are in the custom order, sort by that order
      if (aIndex !== Number.MAX_SAFE_INTEGER && bIndex !== Number.MAX_SAFE_INTEGER) {
        return aIndex - bIndex;
      }
      
      // If one is in the custom order and one isn't, prioritize the one in the order
      if (aIndex !== Number.MAX_SAFE_INTEGER) return -1;
      if (bIndex !== Number.MAX_SAFE_INTEGER) return 1;
      
      // If neither is in the custom order, sort alphabetically
      return a.name.localeCompare(b.name);
    });
  } else {
    // Default alphabetical sorting if no custom order
    sortedGewerkGroups = gewerkGroupsArray.sort((a, b) => a.name.localeCompare(b.name));
  }
  
  // Calculate totals (Hauptsumme — ohne Bedarfs-/Alternativpositionen)
  const totalNet = Object.values(taxGroups).reduce((sum, group) => sum + group.net, 0);
  const totalTax = Object.values(taxGroups).reduce((sum, group) => sum + group.tax, 0);
  const totalGross = totalNet + totalTax;

  // Nachrichtliche Summen der ausgeschlossenen Positionen (für F1-Anzeige).
  const split = computeQuoteTotals(quoteItems);

  return {
    taxGroups,
    gewerkGroups: sortedGewerkGroups,
    totalNet,
    totalTax,
    totalGross,
    // Bedarfs-/Alternativsummen (netto, nachrichtlich neben der Hauptsumme):
    bedarfNet: split.netBedarf,
    alternativNet: split.netAlternativ,
    excludedNet: split.netExcluded,
  };
};
